'use client'
import HotelCenteredMessage from '@/src/components/common/hotels/hotelCenteredMessage/hotelCenteredMessage'
import HotelHeaderCard from '@/src/components/common/hotels/review/hotelHeaderCard'
import HotelInfoSections from '@/src/components/common/hotels/review/hotelInfoSections'
import ImageGallery from '@/src/components/common/hotels/review/imageGallery'
import MapSection from '@/src/components/common/hotels/review/mapSection'
import RoomSelectionSection from '@/src/components/common/hotels/review/roomSelectionSection'
import DetailsShimmer from '@/src/components/common/hotels/review/detailsShimmer'
import { ReviewTab } from '@/src/components/mobile/hotels/review/reviewTabBar'
import { HotelReviewViewModel } from '@/src/types/hotels/details'
import StarIcon from '@mui/icons-material/Star'
import { Box, Button, Container, Typography } from '@mui/material'
import { useCallback, useRef, useState } from 'react'

export interface HotelDetailsDesktopProps {
    viewModel: HotelReviewViewModel | null
    isLoading: boolean
    isError: boolean
    errorTitle: string
    errorSubtitle: string
    errorCtaLabel?: string
    onErrorAction?: () => void
    tabLabels: Record<ReviewTab, string>
    photosCountLabel: (count: number) => string
    viewPhotosLabel: string
    fromPriceText: string
    fromLabel: string
    selectRoomLabel: string
    /** Stay-date picker (modal, own logic); opened by the header card's date row. */
    datePicker?: React.ReactNode
    onEditDates?: () => void
    /**
     * Tabs to offer, in order. Defaults to all three. The property page drops 'rooms'
     * until the guest has searched, because there are no prices to show yet.
     */
    availableTabs?: ReviewTab[]
    /** Tab selected on first render. Defaults to the first available one. */
    defaultTab?: ReviewTab
    /** Render the info sections open, for pages entered from a search engine. */
    expandAll?: boolean
    /** Labels the search control instead of showing a bare icon. */
    searchLabel?: string
    /** Runs the search. Only the property (SEO) page supplies one. */
    onSearch?: () => void
    /** Rooms-and-guests control. Only the property (SEO) page supplies one. */
    guestsField?: React.ReactNode
    /** Trail rendered above the content. The booking flow has none — it is entered from a
     *  search the guest already ran, not from a place in the site's hierarchy. */
    breadcrumb?: React.ReactNode
    onSelectRoom: (rId: string, arNo: string) => void
}

// Desktop hotel review/details page: gallery + hotel header + section tabs in the main
// column, a sticky booking summary rail alongside. Best-of the old desktop layout.
const ALL_REVIEW_TABS: ReviewTab[] = ['rooms', 'info', 'map']

const HotelDetailsDesktop: React.FC<HotelDetailsDesktopProps> = ({
    viewModel,
    isLoading,
    isError,
    errorTitle,
    errorSubtitle,
    errorCtaLabel,
    onErrorAction,
    tabLabels,
    photosCountLabel,
    viewPhotosLabel,
    fromPriceText,
    fromLabel,
    selectRoomLabel,
    datePicker,
    onEditDates,
    availableTabs,
    defaultTab,
    expandAll = false,
    searchLabel,
    onSearch,
    guestsField,
    breadcrumb,
    onSelectRoom
}) => {
    const [activeSection, setActiveSection] = useState<ReviewTab>(defaultTab ?? (availableTabs ?? ALL_REVIEW_TABS)[0])
    const [lastDefaultTab, setLastDefaultTab] = useState(defaultTab)

    // Follow the tab the page asks for when it changes: the property page moves to rooms
    // once a search has run, rather than leaving the guest on the overview.
    if (defaultTab && defaultTab !== lastDefaultTab) {
        setLastDefaultTab(defaultTab)
        setActiveSection(defaultTab)
    }
    const sectionsAnchorRef = useRef<HTMLDivElement>(null)

    const canShowRooms = (availableTabs ?? ALL_REVIEW_TABS).includes('rooms')

    // "Select room" sits beside the gallery, above the fold. `rooms` is already the
    // default tab, so switching alone is a no-op — scroll the section into view too.
    // Where there is no rooms tab yet (the property page before a search) there is nothing
    // to scroll to, so the button prices the stay already on screen.
    const handleSelectRoom = useCallback(() => {
        if (!canShowRooms) {
            ;(onSearch ?? onEditDates)?.()
            return
        }
        setActiveSection('rooms')
        sectionsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [canShowRooms, onSearch, onEditDates])

    if (isLoading) {
        return <DetailsShimmer variant="desktop" />
    }

    if (isError || !viewModel) {
        return (
            <HotelCenteredMessage
                title={errorTitle}
                subtitle={errorSubtitle}
                actionLabel={errorCtaLabel}
                onAction={onErrorAction}
            />
        )
    }

    const reviewContext = {
        productId: viewModel.productId,
        uid: viewModel.uid,
        echoToken: viewModel.echoToken
    }

    const tabs = (availableTabs ?? ALL_REVIEW_TABS).map(key => ({ key, label: tabLabels[key] }))

    return (
        <Box sx={{ bgcolor: 'neutral.100', minHeight: 'calc(100vh - 54px)' }}>
            <Container maxWidth="xl" sx={{ px: 2, py: 3 }}>
                {breadcrumb}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Box
                        component="main"
                        sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        <Box sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                            <ImageGallery
                                variant="desktop"
                                images={viewModel.hotel.images}
                                alt={`${viewModel.hotel.name}, ${viewModel.hotel.cityName}`}
                                photosCountLabel={photosCountLabel}
                                viewPhotosLabel={viewPhotosLabel}
                                galleryCategories={viewModel.hotel.galleryCategories}
                            />
                        </Box>

                        <Box
                            sx={{
                                bgcolor: 'neutral.white',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1.5
                            }}
                        >
                            <HotelHeaderCard
                                variant="wide"
                                hotel={viewModel.hotel}
                                stay={viewModel.stay}
                                onEditDates={onEditDates}
                                searchLabel={searchLabel}
                                onSearch={onSearch}
                                guestsField={guestsField}
                            />
                        </Box>

                        {datePicker}

                        <Box
                            ref={sectionsAnchorRef}
                            sx={{
                                position: 'sticky',
                                top: '54px',
                                zIndex: 5,
                                display: 'flex',
                                gap: 1,
                                bgcolor: 'neutral.100',
                                py: 1,
                                scrollMarginTop: '54px'
                            }}
                        >
                            {tabs.map(tab => {
                                const active = tab.key === activeSection
                                return (
                                    <Box
                                        key={tab.key}
                                        component="button"
                                        type="button"
                                        onClick={() => setActiveSection(tab.key)}
                                        sx={{
                                            border: 1,
                                            borderColor: active ? 'primary.main' : 'borders.default',
                                            bgcolor: active ? 'primary.main' : 'neutral.white',
                                            color: active ? 'neutral.white' : 'textColors.primary',
                                            borderRadius: 999,
                                            px: 2,
                                            py: 0.75,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'inherit' }}>
                                            {tab.label}
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Box>

                        {canShowRooms && activeSection === 'rooms' && (
                            <RoomSelectionSection
                                roomGroups={viewModel.roomGroups}
                                variant="desktop"
                                reviewContext={reviewContext}
                                onSelect={onSelectRoom}
                            />
                        )}
                        {activeSection === 'info' && (
                            <HotelInfoSections
                                info={viewModel.info}
                                tags={viewModel.hotel.tags}
                                numOfRooms={viewModel.hotel.numOfRooms}
                                numOfFloors={viewModel.hotel.numOfFloors}
                                expandAll={expandAll}
                            />
                        )}
                        {activeSection === 'map' && (
                            <MapSection
                                geo={viewModel.hotel.geo}
                                hotelName={viewModel.hotel.name}
                                address={viewModel.hotel.address}
                            />
                        )}
                    </Box>

                    <Box
                        component="aside"
                        sx={{
                            width: 320,
                            flexShrink: 0,
                            position: 'sticky',
                            top: '70px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: 'neutral.white',
                                border: 1,
                                borderColor: 'borders.default',
                                borderRadius: 1.5,
                                p: 2
                            }}
                        >
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'textColors.primary' }}>
                                {viewModel.hotel.name}
                            </Typography>
                            {viewModel.hotel.starCount > 0 && (
                                <Box sx={{ display: 'flex', mt: 0.5 }}>
                                    {Array.from({ length: viewModel.hotel.starCount }, (_, index) => (
                                        <StarIcon key={index} sx={{ fontSize: 14, color: 'warning.main' }} />
                                    ))}
                                </Box>
                            )}
                            {fromPriceText && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography sx={{ fontSize: 12, color: 'textColors.secondary' }}>
                                        {fromLabel}
                                    </Typography>
                                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'textColors.primary' }}>
                                        {fromPriceText}
                                    </Typography>
                                </Box>
                            )}
                            <Button
                                fullWidth
                                variant="customVariantPrimary"
                                onClick={handleSelectRoom}
                                sx={{ mt: 2, py: 1.25, fontSize: 15, fontWeight: 700 }}
                            >
                                {selectRoomLabel}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { HotelDetailsDesktop }
export default HotelDetailsDesktop
